import { useCallback, useState } from 'react';

const STORAGE_KEY = 'jarvis.fileBrowser.previewPopupSize';
export const DEFAULT_PREVIEW_POPUP_W = 960;
export const DEFAULT_PREVIEW_POPUP_H = 720;
const MIN_W = 320;
const MIN_H = 240;
const MAX_VIEWPORT_RATIO = 0.9;
const MAXIMIZE_RATIO = 0.98;

export interface PopupSize {
  w: number;
  h: number;
}

function clampPopupDimensions(w: number, h: number, maxRatio = MAX_VIEWPORT_RATIO): PopupSize {
  if (typeof window === 'undefined') {
    return {
      w: Number(w) || DEFAULT_PREVIEW_POPUP_W,
      h: Number(h) || DEFAULT_PREVIEW_POPUP_H
    };
  }
  const maxW = Math.floor(window.innerWidth * maxRatio);
  const maxH = Math.floor(window.innerHeight * maxRatio);
  return {
    w: Math.min(Math.max(MIN_W, Math.round(Number(w) || DEFAULT_PREVIEW_POPUP_W)), maxW),
    h: Math.min(Math.max(MIN_H, Math.round(Number(h) || DEFAULT_PREVIEW_POPUP_H)), maxH)
  };
}

function maximizedSize(): PopupSize {
  if (typeof window === 'undefined') {
    return { w: DEFAULT_PREVIEW_POPUP_W, h: DEFAULT_PREVIEW_POPUP_H };
  }
  return {
    w: Math.floor(window.innerWidth * MAXIMIZE_RATIO),
    h: Math.floor(window.innerHeight * MAXIMIZE_RATIO)
  };
}

function readStoredSize(): PopupSize {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return clampPopupDimensions(DEFAULT_PREVIEW_POPUP_W, DEFAULT_PREVIEW_POPUP_H);
    const parsed = JSON.parse(raw) as { width?: number; height?: number };
    return clampPopupDimensions(
      parsed.width ?? DEFAULT_PREVIEW_POPUP_W,
      parsed.height ?? DEFAULT_PREVIEW_POPUP_H
    );
  } catch {
    return clampPopupDimensions(DEFAULT_PREVIEW_POPUP_W, DEFAULT_PREVIEW_POPUP_H);
  }
}

function persistSize(next: PopupSize) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ width: next.w, height: next.h }));
  } catch {
    /* ignore quota */
  }
}

function parsePopupDimension(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const n = parseInt(value, 10);
    if (Number.isFinite(n)) return n;
  }
  return undefined;
}

export function useMediaPreviewPopupSize() {
  const [size, setSize] = useState<PopupSize>(() => readStoredSize());
  const [isMaximized, setIsMaximized] = useState(false);

  const onResizeEnd = useCallback(
    (e: { component?: { option: (name: string) => unknown } }) => {
      if (isMaximized) return;
      const w = parsePopupDimension(e.component?.option('width'));
      const h = parsePopupDimension(e.component?.option('height'));
      if (w === undefined || h === undefined) return;
      const next = clampPopupDimensions(w, h);
      setSize(next);
      persistSize(next);
    },
    [isMaximized]
  );

  const maximize = useCallback(() => {
    setIsMaximized(true);
  }, []);

  const restoreDefault = useCallback(() => {
    const next = clampPopupDimensions(DEFAULT_PREVIEW_POPUP_W, DEFAULT_PREVIEW_POPUP_H);
    setIsMaximized(false);
    setSize(next);
    persistSize(next);
  }, []);

  const displaySize = isMaximized ? maximizedSize() : size;

  return {
    size: displaySize,
    isMaximized,
    maximize,
    restoreDefault,
    onResizeEnd,
    maxWidth: isMaximized ? '98vw' : '90vw',
    maxHeight: isMaximized ? '98vh' : '90vh'
  };
}
